import { toast } from "sonner";
import { toastErrorWithHelp } from "@/lib/toastErrorWithHelp";

/**
 * Session persistence — extracted verbatim from ELPMStudioDAW.
 * Save / load the mixer + FX + automation snapshot in localStorage and the
 * iter-265 "New Project" blank-canvas reset.
 */
export default function useSessionPersistence({
  engine,
  masterVol,
  setMasterVol,
  ceiling,
  setCeiling,
  tempo,
  setTempo,
  musicalKey,
  handleKeyChange,
  activeEffects,
  fxParamsByChannel,
  toggleEffectOnChannel,
  setLoadedCount,
  setMaxDur,
  setLoopEnabled,
  setLoopRange,
  setSelectedChannel,
}) {
  const handleSave = () => {
    const session = {
      version: 2,  // bumped from 1 — now includes automation + sidechain + fx params
      savedAt: new Date().toISOString(),
      master: { volume: masterVol, ceiling },
      tempo,
      musicalKey,
      channels: engine.channels.map((c, i) => ({
        idx: i,
        name: c.name,
        volume: c.volume,
        pan: c.pan,
        muted: c.muted,
        solo: c.solo,
        hasAudio: !!c.buffer,
        sidechain: { ...c.sidechain },
        automation: Object.keys(c.automation || {}).length
          ? JSON.parse(JSON.stringify(c.automation))
          : undefined,
        automationArmed: !!c.automationArmed,
      })),
      effects: activeEffects,
      fxParams: fxParamsByChannel,
    };
    try {
      localStorage.setItem("elpm-session", JSON.stringify(session));
      toast.success("Session saved", {
        description: "Includes mixer + FX + automation + sidechain settings.",
      });
    } catch (e) {
      toastErrorWithHelp("Could not save session", {
        description: e?.message,
        probe: "backend",
      });
    }
  };

  // iter-265 — Start a fresh project. Destructive — confirmation toast with
  // an explicit "New Project" action so it can never fire by accident.
  const handleNewProject = () => {
    const loadedCount = engine.channels.filter((c) => !!c.buffer).length;
    const proceed = () => {
      try { engine.stop?.(); } catch (e) { void e; }
      for (let i = 0; i < engine.channels.length; i++) {
        const c = engine.channels[i];
        try { c.stop?.(); } catch (e) { void e; }
        c.buffer = null;
        c.name = null;
        c.waveform = null;
        if (c.clips) c.clips = [];
        c.volume = 72;
        c.pan = 0;
        c.muted = false;
        c.solo = false;
        c.pitchBendSemi = 0;
        c.stretchFactor = 1.0;
        c.playbackRate = 1;
        c.internalLoop = false;
      }
      engine.maxDuration = 0;
      engine.position = 0;
      try { engine._pristineBuffers?.clear?.(); } catch (e) { void e; }
      try { engine.setLoop?.(false, 0, 0); } catch (e) { void e; }
      setLoadedCount(0);
      setMaxDur(0);
      setLoopEnabled(false);
      setLoopRange({ start: 0, end: 0 });
      setSelectedChannel(0);
      try { localStorage.removeItem("elpm-session"); } catch (e) { void e; }
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { void e; }
      toast.success("Started new project", {
        description: "Mixer cleared · tempo, key, loop reset · ready for a fresh take.",
      });
    };
    if (loadedCount === 0) {
      proceed();
      return;
    }
    toast.warning(`Start a new project? ${loadedCount} channel${loadedCount === 1 ? "" : "s"} will be cleared.`, {
      description: "Save your current session first via the Save button if you want to come back to it.",
      duration: 12000,
      action: {
        label: "New Project",
        onClick: proceed,
      },
    });
  };

  /** Load the last-saved session from localStorage. Applies mixer / FX /
   *  automation / sidechain back onto the engine; doesn't restore audio
   *  buffers (too large for localStorage). */
  const handleLoadSession = () => {
    let raw;
    try { raw = localStorage.getItem("elpm-session"); } catch (e) { void e; return; }
    if (!raw) {
      toast.message("No saved session", { description: "Save one first via the Save Session button or Ctrl+K." });
      return;
    }
    let session;
    try { session = JSON.parse(raw); } catch {
      toast.error("Saved session is corrupted");
      return;
    }
    if (!session?.channels) {
      toast.error("Saved session is missing channel data");
      return;
    }
    if (typeof session.tempo === "number") setTempo(session.tempo);
    if (session.musicalKey) handleKeyChange(session.musicalKey);
    if (session.master) {
      setMasterVol(session.master.volume ?? masterVol);
      setCeiling(session.master.ceiling ?? ceiling);
    }
    engine._collabSilent = true;
    try {
      session.channels.forEach((c, i) => {
        const idx = typeof c.idx === "number" ? c.idx : i;
        const ch = engine.channels[idx];
        if (!ch) return;
        if (typeof c.volume === "number") engine.setChannelVolume(idx, c.volume);
        if (typeof c.pan === "number") engine.setChannelPan(idx, c.pan);
        if (typeof c.muted === "boolean") engine.setChannelMute(idx, c.muted);
        if (typeof c.solo === "boolean") engine.setChannelSolo(idx, c.solo);
        if (c.sidechain) engine.setSidechain(idx, c.sidechain);
        if (c.automation) {
          ch.automation = JSON.parse(JSON.stringify(c.automation));
          ch.resetAutomationCursor(0);
        }
        if (c.automationArmed) engine.setChannelAutomationArm(idx, true);
      });
      if (session.fxParams && typeof session.fxParams === "object") {
        for (const [chKey, perChannel] of Object.entries(session.fxParams)) {
          const ci = Number(chKey);
          for (const [effectKey, params] of Object.entries(perChannel || {})) {
            for (const [paramKey, value] of Object.entries(params || {})) {
              if (typeof value === "number") engine.setChannelEffectParam(ci, effectKey, paramKey, value);
            }
          }
        }
      }
      if (session.effects) {
        for (const [chKey, fxMap] of Object.entries(session.effects)) {
          const ci = Number(chKey);
          for (const [effectKey, on] of Object.entries(fxMap || {})) {
            if (on) toggleEffectOnChannel(ci, effectKey);
          }
        }
      }
    } finally {
      engine._collabSilent = false;
    }
    toast.success("Session loaded", {
      description: `Restored ${session.channels.length} channels + FX + automation`,
    });
  };

  return { handleSave, handleNewProject, handleLoadSession };
}
